/**
 * Merchant workspace "entered" state
 * Distinct from `currentMerchant` (src/models/merchant.ts), which is just the
 * last-selected/default merchant. This flag gates whether the merchant-scoped
 * sidebar/workspace (Dashboard, Balance Accounts, Transactions, Payouts,
 * Reports, Disputes) is shown, or whether the user is still on the no-sidebar
 * merchant list.
 */
import { history, useModel } from '@umijs/max';
import { useCallback } from 'react';

const getStorageKey = (userId: string | number | undefined) =>
  userId
    ? `aimerpay_workspace_entered_${userId}`
    : 'aimerpay_workspace_entered';

export function getEnteredMerchantId(userId?: string | number): number | null {
  const raw = sessionStorage.getItem(getStorageKey(userId));
  if (!raw) return null;
  const id = Number.parseInt(raw, 10);
  return Number.isNaN(id) ? null : id;
}

export function setEnteredMerchantId(
  userId: string | number | undefined,
  merchantId: number,
): void {
  sessionStorage.setItem(getStorageKey(userId), String(merchantId));
}

export function clearEnteredMerchantId(userId?: string | number): void {
  sessionStorage.removeItem(getStorageKey(userId));
}

export default function useMerchantWorkspaceModel(): {
  isEntered: boolean;
  enterWorkspace: (merchantId: number) => void;
  exitWorkspace: () => void;
} {
  const merchantModel = useModel('merchant');
  const { initialState, setInitialState } = useModel('@@initialState');
  const userId =
    initialState?.currentUser?.id || initialState?.currentUser?.userid;

  const isEntered = getEnteredMerchantId(userId) !== null;

  const enterWorkspace = useCallback(
    (merchantId: number) => {
      merchantModel.changeMerchant(merchantId);
      setEnteredMerchantId(userId, merchantId);
      // The sidebar now depends on the entered merchant's role, and umi only
      // re-evaluates access when initialState changes — nudge it so switching
      // between an owned and a read-only-granted merchant redraws the menu.
      setInitialState((prev: any) => ({ ...prev }));
      history.push('/dashboard');
    },
    [merchantModel, userId, setInitialState],
  );

  const exitWorkspace = useCallback(() => {
    clearEnteredMerchantId(userId);
    history.push('/merchants/list');
  }, [userId]);

  return {
    isEntered,
    enterWorkspace,
    exitWorkspace,
  };
}
